import { useParams } from 'react-router-dom';
import { useAnalysisData, useMessage } from 'pages/trading/queryHooks';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { Box, Container } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { PriceStandardDetailPage } from './PriceStandardDetailPage';
import { PricePromotionDetailPage } from 'pages/trading/price/promotion/PricePromotionDetailPage';
import { PriceChange } from 'pages/trading/model';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { FormattedMessage } from 'react-intl';
import { getErrorMessage } from 'utils/getErrorMessage';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';

export function PriceDetailPage() {
  const { messageId } = useParams<{ messageId: string }>();

  const setDetailPageBreadcrumb = useBreadcrumbStore(state => state.setDetailPageBreadcrumb);

  const {
    data: message,
    isPending: messageIsPending,
    isError: messageIsError,
    error: messageError,
  } = useMessage(messageId);
  const {
    data: analysisData,
    isPending: dataIsPending,
    isError: dataIsError,
    error: dataError,
  } = useAnalysisData(message?.analysisDataId);

  useEffect(() => {
    if (message) {
      setDetailPageBreadcrumb(location.pathname, [
        { attribute: 'messageId', values: [messageId ?? ''] },
        { attribute: 'dateRange', values: [message.currentDataVersion] },
      ]);
    }
  }, [message, messageId, location.pathname, setDetailPageBreadcrumb]);

  const { filterType, priceChangeDate, displayMessage } = useMemo(() => {
    if (!message) return { filterType: '', priceChangeDate: undefined, displayMessage: null };

    const typeRelationship = message.relationships?.find(r => r.level === 'CHANGE_TYPE');
    const type = typeRelationship ? typeRelationship.levelKey.toLowerCase() : '';

    const dateRelationship = message.relationships?.find(r => r.level === 'PRICE_CHANGE_DATE');
    const date = dateRelationship?.levelKey;

    return { filterType: type, priceChangeDate: date, displayMessage: message };
  }, [message]);

  const filteredData = useMemo(() => {
    if (!analysisData?.data) return [];

    return (analysisData.data as PriceChange[]).filter(row => {
      const typeMatch = !filterType || row.changeType?.toLowerCase() === filterType;
      if (!priceChangeDate) return typeMatch;

      const fromDateMs = row.toPrice?.fromDate;
      const dateMatch = fromDateMs !== undefined && new Date(fromDateMs).toISOString().slice(0, 10) === priceChangeDate;

      return typeMatch && dateMatch;
    });
  }, [analysisData, filterType, priceChangeDate]);

  if (messageIsPending || dataIsPending) {
    return (
      <Box className="flex justify-center py-20">
        <LoadingSpinner />
      </Box>
    );
  }

  if (messageIsError || dataIsError) {
    return (
      <Container maxWidth={false} className="standard-page-container">
        <PageContainer>
          <PageSectionTitle>
            <FormattedMessage id="trading.price_analysis.title" defaultMessage="Price Analysis" />
          </PageSectionTitle>
          <Tile
            errorDetails={getErrorMessage(messageError) ?? getErrorMessage(dataError)}
            testId="trading-price-analysis-error"
          />
        </PageContainer>
      </Container>
    );
  }

  if (!displayMessage || !filteredData || filteredData.length === 0) {
    return (
      <div className="p-10 text-center text-gray-500">
        <FormattedMessage
          id="trading.price_change.no_details_available"
          defaultMessage="No details available for this message."
        />
      </div>
    );
  }

  if (filterType === 'promotion') {
    return <PricePromotionDetailPage message={displayMessage} data={filteredData} />;
  }

  return <PriceStandardDetailPage message={displayMessage} data={filteredData} />;
}
